import {
    ActionMenu,
    CustomActionMenuItem,
} from '@/common/components/ActionMenu';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { hasEditPermission } from '@/common/constants/member';
import { useCommonStyles, sidebarTabsListProps } from '@/common/theme/css';
import {
    Team,
    Workspace,
    WorkspaceWithInfo,
} from '@/common/types/databaseTypes';
import { useReportsWithReceiverByUserId } from '@/features/performance/report/personalreport/services';
import type { ReportWithDetails } from '@/features/performance/report/shared/types/reportTypes';
import CreateTeamDialog from '@/features/team/components/Dialog/CreateTeamDialog';
import { TeamItem } from '@/features/team/components/TeamItem';
import { useMyTeamsByWorkspaceId } from '@/features/team/services/useTeamQueries';
import { CreateWorkspaceDialog } from '@/features/workspace';
import { useCurrentWorkspace } from '@/features/workspace/hooks/useCurrentWorkspace';
import { useAuthStore } from '@/stores/authStore';
import { useSecondarySidebarStore } from '@/common/stores/secondarySidebarStore';
import {
    ActionIcon,
    Badge,
    Divider,
    Group,
    Stack,
    Tabs,
    Text,
    Tooltip,
    useMantineTheme,
} from '@mantine/core';
import {
    IconCheck,
    IconChevronDown,
    IconCircles,
    IconFileStar,
    IconLayoutSidebar,
    IconPlus,
    IconRefresh,
    IconSettings,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMyWorkspaces } from '../services';
import { useMyProjects } from '@/features/project/services/useProjectQueries';
import ProjectList from '@/features/project/components/SidebarProjectList';
import { useStackModalStore } from '@/common/stores/modalStore';
import { Icon, ICONS } from '@/common/components/Icon';
import { useSidebarUnreadStore } from '@/stores/sidebarUnreadStore';

interface WorkspaceItemProps {
    workspace: Workspace;
}

const DIVIDER_STYLE = '0.5rem 0';
const SUB_DIVIDER_STYLE = '.5rem .5rem .25rem .5rem';

export function WorkspaceItem({ workspace }: WorkspaceItemProps) {
    const theme = useMantineTheme();
    const { t } = useTranslation(['workspace', 'project']);
    const commonStyles = useCommonStyles();

    const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
    const [isCreateWorkspaceDialogOpen, setIsCreateWorkspaceDialogOpen] =
        useState(false);
    const [isHoveredWorkspace, setIsHoveredWorkspace] = useState(false);

    // SecondarySidebar 접기/펴기 상태 관리
    const { isExpanded, toggleExpanded } = useSecondarySidebarStore();

    const { user } = useAuthStore();
    const { openModal } = useStackModalStore();

    const { data: teams = [], refetch } = useMyTeamsByWorkspaceId(workspace.id);
    const { data: workspaces = [], refetch: refetchWorkspaces } =
        useMyWorkspaces();
    const navigate = useNavigate();
    const { data: projects = [], isLoading: isProjectsLoading } = useMyProjects(
        { workspaceId: workspace.id }
    );

    // 안 읽은 보고서, 피드백 개수 가져오기
    const { unreadReportCount, unreadFeedbackCount } = useSidebarUnreadStore();

    // 팀 관리 권한 확인 (admin 또는 owner만 가능)
    const currentWorkspaceMembers = workspaces.find(ws => ws.id === workspace.id)?.workspace_member ?? [];
    const canManageTeams = hasEditPermission(user?.id, currentWorkspaceMembers);

    const isPersonalReportPage = location.pathname.includes('/report/personal');
    const isTeamReportPage = location.pathname.includes('/report/team');
    const isFeedbackPage = location.pathname.includes('/feedback');

    const handleCloseTeamDialog = () => {
        setIsCreateTeamDialogOpen(false);
        // 팀 생성 다이얼로그가 닫힐 때 팀 목록 새로고침
        void refetch();
    };

    const handleCloseWorkspaceDialog = () => {
        setIsCreateWorkspaceDialogOpen(false);
        // 워크스페이스 생성 다이얼로그가 닫힐 때 워크스페이스 목록 새로고침
        void refetchWorkspaces();
    };

    // 워크스페이스 변경 핸들러
    const handleWorkspaceChange = (ws: WorkspaceWithInfo) => {
        // 워크스페이스 변경 후 URL 업데이트
        void navigate(`/workspace/${ws.id}/setting`, { replace: true });
    };

    const openCreateProjectModal = useCallback(() => {
        openModal('CREATE_PROJECT', {
            workspaceId: workspace.id,
        });
    }, [openModal, workspace.id]);

    // 현재 워크스페이스가 변경됐을 때 해당 워크스페이스의 팀 목록 로드
    // useEffect(() => {
    //     if (workspace?.id) {
    //         void refetch();
    //     }
    // }, [workspace?.id, refetch]);

    return (
        <>
            <Stack gap={0} py="sm">
                {!isExpanded && (
                    // 접기/펼치기 버튼
                    <>
                        <Tooltip
                            label={t('common:action.expand')}
                            withArrow
                            position="right"
                        >
                            <ActionIcon
                                variant="transparent"
                                onClick={toggleExpanded}
                                style={{
                                    cursor: 'pointer',
                                    alignSelf: 'center',
                                }}
                            >
                                <IconLayoutSidebar size={16} color="gray" />
                            </ActionIcon>
                        </Tooltip>
                        <Divider style={{ margin: DIVIDER_STYLE }} />
                    </>
                )}

                {/* 워크스페이스 메뉴 */}
                <Group justify="space-between" mb={4}>
                    <div style={{ flex: 1 }}>
                        <Tooltip
                            label={workspace.name}
                            withArrow
                            position="right"
                            disabled={isExpanded}
                        >
                            <div>
                                <ActionMenu
                                    position="top-end"
                                    actionIcon={
                                        <IconRefresh size={12} color="gray" />
                                    }
                                    withArrow={false}
                                    customMenuTarget={
                                        <Group
                                            justify={
                                                isExpanded ? 'start' : 'center'
                                            }
                                            w={
                                                isExpanded
                                                    ? '100%'
                                                    : 'fit-content'
                                            }
                                            gap={4}
                                            px={isExpanded ? 12 : 8}
                                            py={8}
                                            style={{
                                                borderRadius: theme.radius.md,
                                                cursor: 'pointer',
                                                backgroundColor:
                                                    isHoveredWorkspace
                                                        ? `#868E961a`
                                                        : 'transparent',
                                                margin: isExpanded
                                                    ? '0 0 0 8px'
                                                    : '0 auto',
                                            }}
                                            onMouseEnter={() =>
                                                setIsHoveredWorkspace(true)
                                            }
                                            onMouseLeave={() =>
                                                setIsHoveredWorkspace(false)
                                            }
                                        >
                                            <UserAvatar
                                                name={workspace.name}
                                                imageUrl={
                                                    workspace.profile_image_url
                                                }
                                                size={16}
                                                radius={4}
                                            />
                                            {isExpanded && (
                                                <>
                                                    <Text
                                                        size="xs"
                                                        fw={600}
                                                        flex={1}
                                                        lineClamp={1}
                                                    >
                                                        {workspace.name}
                                                    </Text>
                                                    {isHoveredWorkspace && (
                                                        <IconChevronDown
                                                            size={12}
                                                            color="#868E96"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Group>
                                    }
                                    title={t('component.item.title')}
                                    items={[
                                        {
                                            id: 'workspace-settings',
                                            label: t('component.item.setting'),
                                            icon: (
                                                <IconSettings
                                                    size={12}
                                                    color="gray"
                                                />
                                            ),
                                            onClick: () =>
                                                void navigate(
                                                    `/workspace/${workspace.id}/setting`
                                                ),
                                        },
                                        ...(canManageTeams
                                            ? [
                                                  {
                                                      id: 'create-team',
                                                      label: t(
                                                          'component.item.create_team'
                                                      ),
                                                      icon: (
                                                          <IconCircles
                                                              size={12}
                                                              color="gray"
                                                          />
                                                      ),
                                                      onClick: () =>
                                                          setIsCreateTeamDialogOpen(
                                                              true
                                                          ),
                                                  },
                                              ]
                                            : []),
                                        {
                                            id: 'divider',
                                            isCustom: true,
                                            customComponent: <Divider my={4} />,
                                        } as CustomActionMenuItem,

                                        ...workspaces.map(ws => ({
                                            id: ws.id,
                                            label: ws.name,
                                            icon: (
                                                <UserAvatar
                                                    name={ws.name}
                                                    imageUrl={
                                                        ws.profile_image_url
                                                    }
                                                    size={16}
                                                    radius={4}
                                                />
                                            ),
                                            rightSection:
                                                ws.id === workspace.id ? (
                                                    <IconCheck
                                                        size={14}
                                                        color="gray"
                                                    />
                                                ) : null,
                                            onClick: () => {
                                                handleWorkspaceChange(ws);
                                            },
                                        })),

                                        {
                                            id: 'create-workspace',
                                            label: t(
                                                'component.item.create_new'
                                            ),
                                            icon: (
                                                <IconPlus
                                                    size={14}
                                                    color="gray"
                                                />
                                            ),
                                            onClick: () =>
                                                setIsCreateWorkspaceDialogOpen(
                                                    true
                                                ),
                                        },
                                    ]}
                                />
                            </div>
                        </Tooltip>
                    </div>

                    {isExpanded && (
                        // 접기/펼치기 버튼
                        <Tooltip label={t('common:action.collapse')} withArrow>
                            <ActionIcon
                                variant="transparent"
                                onClick={toggleExpanded}
                                mr={8}
                                style={{ cursor: 'pointer' }}
                            >
                                <IconLayoutSidebar size={16} color="gray" />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>

                {/* 팀 목록 */}
                <Group pl={isExpanded ? 4 : '0'} mx={isExpanded ? 0 : 'auto'}>
                    {teams.length > 0 ? (
                        <Stack w="100%" gap={0} px={8}>
                            {teams.map((team: Team) => (
                                <Tooltip
                                    key={team.id}
                                    label={team.name}
                                    withArrow
                                    position="right"
                                    disabled={isExpanded}
                                >
                                    <div>
                                        <TeamItem team={team} />
                                    </div>
                                </Tooltip>
                            ))}
                        </Stack>
                    ) : isExpanded ? (
                        <Text size="xs" c="dimmed" py={8}>
                            {t('component.item.no_teams')}
                        </Text>
                    ) : null}
                </Group>

                {/* 워크스페이스가 존재하는 경우에만 보고서, 성과관리 섹션 표시 */}
                {workspace?.id && (
                    <>
                        <Divider style={{ margin: DIVIDER_STYLE }} />

                        {isExpanded && (
                            <Text px={12} py={8} size="xs" fw={500} c="gray.6">
                                {t('component.item.report.title')}
                            </Text>
                        )}

                        <Tabs
                            orientation="vertical"
                            variant="pills"
                            value={
                                isPersonalReportPage
                                    ? 'personal'
                                    : isTeamReportPage
                                      ? 'team'
                                      : null
                            }
                            w={isExpanded ? '100%' : 'fit-content'}
                            mx={isExpanded ? 0 : 'auto'}
                            classNames={{
                                tab: commonStyles.classes.secondarySidebarItem,
                            }}
                        >
                            <Tabs.List {...sidebarTabsListProps}>
                                <Tooltip
                                    label={t('component.item.report.personal')}
                                    withArrow
                                    disabled={isExpanded}
                                    position="right"
                                >
                                    <Tabs.Tab
                                        px={isExpanded ? null : 12}
                                        value="personal"
                                        c={
                                            isPersonalReportPage
                                                ? 'gray.8'
                                                : 'gray.6'
                                        }
                                        leftSection={
                                            <UnreadBadgeIcon
                                                icon={
                                                    <Icon
                                                        name={
                                                            ICONS.COMMON
                                                                .PERSONAL_REPORT
                                                        }
                                                        size={16}
                                                        color="currentColor"
                                                    />
                                                }
                                                showBadge={
                                                    unreadReportCount > 0
                                                }
                                            />
                                        }
                                        onClick={() => {
                                            void navigate(
                                                `/workspace/${workspace.id}/report/personal`
                                            );
                                        }}
                                        py="xs"
                                    >
                                        {isExpanded && (
                                            <Text
                                                c={
                                                    isPersonalReportPage
                                                        ? 'black'
                                                        : '#212529'
                                                }
                                                size="xs"
                                                lh={1}
                                                fw={
                                                    isPersonalReportPage
                                                        ? 600
                                                        : 400
                                                }
                                            >
                                                {t(
                                                    'component.item.report.personal'
                                                )}
                                            </Text>
                                        )}
                                    </Tabs.Tab>
                                </Tooltip>

                                {/* 팀 보고서 탭 임시 비활성화 */}
                                {/* <Tabs.Tab
                                    px={isExpanded ? null : 12}
                                    value="team"
                                    c={isTeamReportPage ? 'gray.8' : 'gray.6'}
                                    leftSection={
                                        <UnreadBadgeIcon
                                            icon={
                                                <Icon 
                                                    name={ICONS.COMMON.TEAM_REPORT} 
                                                    size={16}
                                                    color="currentColor"
                                                />
                                            }
                                            showBadge={unreadReportCount > 0}
                                        />
                                    }
                                    onClick={() => {
                                        void navigate(
                                            `/workspace/${workspace.id}/report/team`
                                        );
                                    }}
                                    py="xs"
                                >
                                    {isExpanded && (
                                        <Text
                                            c={
                                                isTeamReportPage
                                                    ? 'black'
                                                    : '#212529'
                                            }
                                            size="xs"
                                            lh={1}
                                            fw={isTeamReportPage ? 600 : 400}
                                        >
                                            {t('component.item.report.team')}
                                        </Text>
                                    )}
                                </Tabs.Tab> */}
                            </Tabs.List>
                        </Tabs>

                        {/* 성과관리 섹션 */}
                        <Divider style={{ margin: SUB_DIVIDER_STYLE }} />

                        {isExpanded && (
                            <Text px={12} py={8} size="xs" fw={500} c="gray.6">
                                {t('component.item.performance')}
                            </Text>
                        )}

                        <Tabs
                            orientation="vertical"
                            variant="pills"
                            value={isFeedbackPage ? 'feedback' : null}
                            w={isExpanded ? '100%' : 'fit-content'}
                            mx={isExpanded ? 0 : 'auto'}
                            mt={isExpanded ? 0 : '.25rem'}
                            classNames={{
                                tab: commonStyles.classes.secondarySidebarItem,
                            }}
                        >
                            <Tabs.List {...sidebarTabsListProps}>
                                <Tooltip
                                    label={t('component.item.feedback')}
                                    withArrow
                                    disabled={isExpanded}
                                    position="right"
                                >
                                    <Tabs.Tab
                                        px={isExpanded ? null : 12}
                                        value="feedback"
                                        c={isFeedbackPage ? 'gray.8' : 'gray.6'}
                                        leftSection={
                                            <UnreadBadgeIcon
                                                icon={
                                                    <IconFileStar size={16} />
                                                }
                                                showBadge={
                                                    unreadFeedbackCount > 0
                                                }
                                            />
                                        }
                                        onClick={() => {
                                            void navigate(
                                                `/workspace/${workspace.id}/feedback`
                                            );
                                        }}
                                    >
                                        {isExpanded && (
                                            <Text
                                                c={
                                                    isFeedbackPage
                                                        ? 'black'
                                                        : '#212529'
                                                }
                                                size="xs"
                                                lh={1}
                                                fw={isFeedbackPage ? 600 : 400}
                                            >
                                                {t('component.item.feedback')}
                                            </Text>
                                        )}
                                    </Tabs.Tab>
                                </Tooltip>
                            </Tabs.List>
                        </Tabs>
                    </>
                )}

                {/* 참여 중인 프로젝트 목록 */}
                {isExpanded && teams.length > 0 && (
                    <>
                        <Divider style={{ margin: SUB_DIVIDER_STYLE }} />
                        <Group justify="space-between">
                            <Text px={12} py={8} size="xs" fw={500} c="gray.6">
                                {t('project:title')}
                            </Text>
                            <Tooltip label={t('project:table.add')} withArrow>
                                <ActionIcon
                                    variant="transparent"
                                    onClick={openCreateProjectModal}
                                    mr={8}
                                >
                                    <IconPlus size={12} color="gray" />
                                </ActionIcon>
                            </Tooltip>
                        </Group>

                        {/* 참여 중인 프로젝트 목록 */}
                        {projects.length > 0 && (
                            <ProjectList projects={projects} />
                        )}
                    </>
                )}
            </Stack>
            <CreateTeamDialog
                opened={isCreateTeamDialogOpen}
                onClose={handleCloseTeamDialog}
                initialWorkspaceId={workspace.id}
            />
            <CreateWorkspaceDialog
                opened={isCreateWorkspaceDialogOpen}
                onClose={handleCloseWorkspaceDialog}
            />
        </>
    );
}

const UnreadBadgeIcon = ({
    icon,
    showBadge,
}: {
    icon: React.ReactNode;
    showBadge: boolean;
}) => {
    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-block',
            }}
        >
            {icon}
            {showBadge && (
                <Badge
                    size="xs"
                    variant="filled"
                    w={6}
                    h={6}
                    p={0}
                    radius="50%"
                    pos="absolute"
                    top="-2px"
                    right="-2px"
                />
            )}
        </div>
    );
};
